import { useEffect, useRef, useState } from "react";
import { api, API, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { testIds } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  LogOut,
  Upload,
  Loader2,
  X,
  Package,
  FileText,
  Mail,
  Phone,
  Calendar as CalIcon,
  Check,
  Ban,
  FileDown,
  RotateCcw,
  CalendarDays,
  Settings as SettingsIcon,
  Users,
  BarChart3,
  Landmark,
  Clock,
  Star,
} from "lucide-react";
import CalendarView from "@/components/CalendarView";
import SettingsView from "@/components/SettingsView";
import BlocksView from "@/components/BlocksView";
import CollaboratorsView from "@/components/CollaboratorsView";
import StatsView from "@/components/StatsView";
import ItemsPendingView from "@/components/ItemsPendingView";
import ReviewsView from "@/components/ReviewsView";

const CATEGORIES = ["Audio", "Luci", "Video", "DJ & FX", "Strutture & Truss"];

const emptyForm = {
  id: null,
  name: "",
  category: "Audio",
  daily_price: 0,
  quantity: 1,
  description: "",
  image_path: null,
};

const STATUS_META = {
  new: {
    label: "Nuovo",
    cls: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  },
  confirmed: {
    label: "Confermato",
    cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  },
  rejected: {
    label: "Rifiutato",
    cls: "bg-zinc-800 text-zinc-400 border-zinc-700",
  },
};

export default function Admin() {
  const { logout } = useAuth();
  const [tab, setTab] = useState("items");
  const [items, setItems] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [selectedQuoteIds, setSelectedQuoteIds] = useState(new Set());
  const fileRef = useRef(null);

  const loadItems = async () => {
    setLoading(true);
    const { data } = await api.get("/items/mine");
    setItems(data);
    setLoading(false);
  };
  const loadQuotes = async () => {
    setLoading(true);
    const { data } = await api.get("/quotes");
    setQuotes(data);
    setSelectedQuoteIds(new Set());
    setLoading(false);
  };

  const toggleQuoteSelect = (id) => {
    setSelectedQuoteIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    setSelectedQuoteIds((prev) =>
      prev.size === quotes.length
        ? new Set()
        : new Set(quotes.map((q) => q.id)),
    );
  };

  const bulkDeleteQuotes = async () => {
    if (selectedQuoteIds.size === 0) return;
    if (
      !window.confirm(
        `Eliminare ${selectedQuoteIds.size} preventivi selezionati? L'operazione è irreversibile.`,
      )
    )
      return;
    try {
      const { data } = await api.post("/quotes/bulk-delete", {
        ids: Array.from(selectedQuoteIds),
      });
      toast.success(`${data.deleted} preventivi eliminati`);
      loadQuotes();
    } catch {
      toast.error("Errore eliminazione multipla");
    }
  };

  useEffect(() => {
    if (tab === "items") loadItems();
    else if (tab === "quotes") loadQuotes();
  }, [tab]);

  const startCreate = () => {
    setForm(emptyForm);
    setShowForm(true);
  };
  const startEdit = (it) => {
    setForm({ ...it });
    setShowForm(true);
  };

  const submitItem = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        daily_price: Number(form.daily_price),
        quantity: Number(form.quantity),
        description: form.description || "",
        image_path: form.image_path,
      };
      if (form.id) {
        await api.put(`/items/${form.id}`, payload);
        toast.success("Articolo aggiornato");
      } else {
        await api.post("/items", payload);
        toast.success("Articolo aggiunto");
      }
      setShowForm(false);
      setForm(emptyForm);
      loadItems();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    await api.delete(`/items/${id}`);
    toast.success("Articolo eliminato");
    loadItems();
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_path: data.path }));
      toast.success("Foto caricata");
    } catch {
      toast.error("Errore caricamento foto");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setQuoteStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}/status`, { status });
      toast.success(
        status === "confirmed"
          ? "Preventivo confermato — articoli riservati"
          : status === "rejected"
            ? "Preventivo rifiutato"
            : "Preventivo riaperto",
      );
      loadQuotes();
    } catch {
      toast.error("Errore aggiornamento stato");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-rose-500">
            Pannello di controllo
          </div>
          <h1 className="font-heading text-2xl sm:text-4xl font-black uppercase tracking-tight text-white">
            Admin <span className="text-rose-500">Spectrum</span>
          </h1>
        </div>
        <button
          data-testid={testIds.adminLogoutBtn}
          onClick={logout}
          className="min-h-[40px] px-4 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-rose-800 text-zinc-300 hover:text-rose-500 text-xs font-semibold uppercase tracking-wider transition flex items-center gap-1.5"
        >
          <LogOut size={14} /> Esci
        </button>
      </div>

      <div className="flex gap-2 mb-6 border-b border-zinc-800 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <button
          data-testid={testIds.adminTabItems}
          onClick={() => setTab("items")}
          className={`shrink-0 min-h-[44px] px-4 text-xs sm:text-sm font-heading font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 ${
            tab === "items"
              ? "border-rose-600 text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Package size={14} /> Articoli ({items.length})
        </button>
        <button
          data-testid={testIds.adminTabQuotes}
          onClick={() => setTab("quotes")}
          className={`shrink-0 min-h-[44px] px-4 text-xs sm:text-sm font-heading font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 ${
            tab === "quotes"
              ? "border-rose-600 text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <FileText size={14} /> Preventivi
        </button>
        <button
          data-testid={testIds.adminTabCalendar}
          onClick={() => setTab("calendar")}
          className={`shrink-0 min-h-[44px] px-4 text-xs sm:text-sm font-heading font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 ${
            tab === "calendar"
              ? "border-rose-600 text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <CalendarDays size={14} /> Calendario
        </button>
        <button
          data-testid={testIds.adminTabBlocks}
          onClick={() => setTab("blocks")}
          className={`shrink-0 min-h-[44px] px-4 text-xs sm:text-sm font-heading font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 ${
            tab === "blocks"
              ? "border-rose-600 text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Ban size={14} /> Blocchi
        </button>
        <button
          data-testid={testIds.adminTabCollaborators}
          onClick={() => setTab("collabs")}
          className={`shrink-0 min-h-[44px] px-4 text-xs sm:text-sm font-heading font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 ${
            tab === "collabs"
              ? "border-rose-600 text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Users size={14} /> Collaboratori
        </button>
        <button
          data-testid={testIds.adminTabPending}
          onClick={() => setTab("pending")}
          className={`shrink-0 min-h-[44px] px-4 text-xs sm:text-sm font-heading font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 ${
            tab === "pending"
              ? "border-rose-600 text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Clock size={14} /> In verifica
        </button>
        <button
          data-testid={testIds.adminTabStats}
          onClick={() => setTab("stats")}
          className={`shrink-0 min-h-[44px] px-4 text-xs sm:text-sm font-heading font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 ${
            tab === "stats"
              ? "border-rose-600 text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <BarChart3 size={14} /> Numeri
        </button>
        <button
          data-testid={testIds.adminTabReviews}
          onClick={() => setTab("reviews")}
          className={`shrink-0 min-h-[44px] px-4 text-xs sm:text-sm font-heading font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 ${
            tab === "reviews"
              ? "border-rose-600 text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Star size={14} /> Recensioni
        </button>
        <button
          data-testid={testIds.adminTabSettings}
          onClick={() => setTab("settings")}
          className={`shrink-0 min-h-[44px] px-4 text-xs sm:text-sm font-heading font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 ${
            tab === "settings"
              ? "border-rose-600 text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <SettingsIcon size={14} /> Impostazioni
        </button>
      </div>

      {tab === "calendar" && <CalendarView />}
      {tab === "blocks" && <BlocksView />}
      {tab === "collabs" && <CollaboratorsView />}
      {tab === "stats" && <StatsView />}
      {tab === "pending" && <ItemsPendingView />}
      {tab === "reviews" && <ReviewsView />}
      {tab === "settings" && <SettingsView />}

      {tab === "items" && (
        <>
          <button
            data-testid={testIds.adminAddItemBtn}
            onClick={startCreate}
            className="mb-4 min-h-[44px] px-4 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-heading font-black uppercase tracking-wider text-xs sm:text-sm shadow-lg shadow-rose-900/40 transition flex items-center gap-2"
          >
            <Plus size={16} /> Nuovo articolo
          </button>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-zinc-500">
              <Loader2 className="animate-spin mr-2" size={18} /> Caricamento...
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {items.map((it) => (
                <div
                  key={it.id}
                  className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-3 sm:p-4 flex items-center gap-3"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-zinc-950 border border-zinc-800 overflow-hidden shrink-0 flex items-center justify-center">
                    {it.image_path ? (
                      <img
                        src={fileUrl(it.image_path)}
                        alt={it.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Package size={20} className="text-zinc-700" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-white text-sm truncate">
                      {it.name}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                        {it.category}
                      </span>
                      <span className="text-[10px] font-mono text-zinc-600">
                        · qty {it.quantity}
                      </span>
                      <span className="font-mono font-bold text-emerald-400 text-xs sm:text-sm">
                        · €{it.daily_price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <button
                    data-testid={testIds.adminEditItem(it.id)}
                    onClick={() => startEdit(it)}
                    className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition"
                    aria-label="Modifica"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    data-testid={testIds.adminDeleteItem(it.id)}
                    onClick={() => deleteItem(it.id)}
                    className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-800 hover:bg-rose-950 text-zinc-300 hover:text-rose-400 transition"
                    aria-label="Elimina"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "quotes" && (
        <div className="space-y-3">
          {!loading && quotes.length > 0 && (
            <div className="flex items-center gap-3 bg-zinc-900/90 border border-zinc-800 rounded-xl p-3">
              <label className="flex items-center gap-2 text-xs text-zinc-300 font-mono uppercase tracking-widest cursor-pointer">
                <input
                  data-testid={testIds.adminQuoteSelectAll}
                  type="checkbox"
                  checked={
                    selectedQuoteIds.size === quotes.length && quotes.length > 0
                  }
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded accent-rose-600"
                />
                Seleziona tutti ({selectedQuoteIds.size}/{quotes.length})
              </label>
              <button
                data-testid={testIds.adminQuoteBulkDelete}
                onClick={bulkDeleteQuotes}
                disabled={selectedQuoteIds.size === 0}
                className="ml-auto min-h-[36px] px-3 rounded-lg bg-rose-600/15 hover:bg-rose-600/25 disabled:opacity-40 disabled:cursor-not-allowed border border-rose-500/40 text-rose-300 text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5"
              >
                <Trash2 size={12} /> Elimina selezionati
              </button>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-12 text-zinc-500">
              <Loader2 className="animate-spin mr-2" size={18} /> Caricamento...
            </div>
          ) : quotes.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 text-sm">
              Nessun preventivo ricevuto.
            </div>
          ) : (
            quotes.map((q) => {
              const meta = STATUS_META[q.status] || STATUS_META.new;
              const dates =
                q.event_start_date && q.event_end_date
                  ? q.event_start_date === q.event_end_date
                    ? q.event_start_date
                    : `${q.event_start_date} → ${q.event_end_date}`
                  : q.event_start_date || q.event_date || "-";
              return (
                <div
                  key={q.id}
                  className={`bg-zinc-900/90 border rounded-xl p-4 ${selectedQuoteIds.has(q.id) ? "border-rose-600/60" : "border-zinc-800"}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <input
                      data-testid={testIds.adminQuoteSelect(q.id)}
                      type="checkbox"
                      checked={selectedQuoteIds.has(q.id)}
                      onChange={() => toggleQuoteSelect(q.id)}
                      className="w-4 h-4 rounded accent-rose-600 mt-1.5 shrink-0"
                    />
                    <div className="flex-1 min-w-0 flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading font-bold text-white text-base break-words">
                            {q.customer_name}
                          </span>
                          <span
                            data-testid={testIds.adminQuoteStatus(q.id)}
                            className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border ${meta.cls}`}
                          >
                            {meta.label}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-zinc-500">
                          {q.customer_email && (
                            <a
                              href={`mailto:${q.customer_email}`}
                              className="flex items-center gap-1 hover:text-rose-400 break-all"
                            >
                              <Mail size={11} /> {q.customer_email}
                            </a>
                          )}
                          {q.customer_phone && (
                            <a
                              href={`tel:${q.customer_phone}`}
                              className="flex items-center gap-1 hover:text-rose-400"
                            >
                              <Phone size={11} /> {q.customer_phone}
                            </a>
                          )}
                          <span className="flex items-center gap-1">
                            <CalIcon size={11} /> {dates}
                          </span>
                          <span className="font-mono">
                            {q.days} {q.days === 1 ? "giorno" : "giorni"}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                          Totale
                        </div>
                        <div className="font-mono font-black text-xl text-rose-500">
                          €{q.total.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-zinc-800 pt-3 space-y-1">
                    {q.lines.map((l, i) => (
                      <div
                        key={i}
                        className="flex justify-between text-xs text-zinc-400 font-mono"
                      >
                        <span>
                          {l.quantity}× {l.name}
                        </span>
                        <span>
                          €{(l.daily_price * l.quantity).toFixed(2)}/gg
                        </span>
                      </div>
                    ))}
                  </div>
                  {q.notes && (
                    <div className="mt-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 rounded-lg p-3">
                      {q.notes}
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-zinc-800 flex flex-wrap gap-2">
                    {q.status !== "confirmed" && (
                      <button
                        data-testid={testIds.adminQuoteConfirm(q.id)}
                        onClick={() => setQuoteStatus(q.id, "confirmed")}
                        className="min-h-[36px] px-3 rounded-lg bg-emerald-600/15 hover:bg-emerald-600/25 border border-emerald-500/40 text-emerald-300 text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5"
                      >
                        <Check size={12} /> Conferma
                      </button>
                    )}
                    {q.status !== "rejected" && (
                      <button
                        data-testid={testIds.adminQuoteReject(q.id)}
                        onClick={() => setQuoteStatus(q.id, "rejected")}
                        className="min-h-[36px] px-3 rounded-lg bg-zinc-800 hover:bg-rose-950 border border-zinc-700 text-zinc-400 hover:text-rose-400 text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5"
                      >
                        <Ban size={12} /> Rifiuta
                      </button>
                    )}
                    {q.status !== "new" && (
                      <button
                        data-testid={testIds.adminQuoteReopen(q.id)}
                        onClick={() => setQuoteStatus(q.id, "new")}
                        className="min-h-[36px] px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5"
                      >
                        <RotateCcw size={12} /> Riapri
                      </button>
                    )}
                    <a
                      data-testid={testIds.adminQuotePdf(q.id)}
                      href={`${API}/quotes/${q.id}/pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="min-h-[36px] px-3 rounded-lg bg-rose-600/15 hover:bg-rose-600/25 border border-rose-500/40 text-rose-300 text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5 ml-auto"
                    >
                      <FileDown size={12} /> PDF
                    </a>
                    <a
                      data-testid={testIds.adminInternalPdf(q.id)}
                      href={`${API}/quotes/${q.id}/internal-pdf?t=${encodeURIComponent(localStorage.getItem("spectrum_admin_token") || "")}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        fetch(`${API}/quotes/${q.id}/internal-pdf`, {
                          headers: {
                            Authorization: `Bearer ${localStorage.getItem("spectrum_admin_token")}`,
                          },
                        })
                          .then((r) => r.blob())
                          .then((b) => {
                            const url = URL.createObjectURL(b);
                            window.open(url, "_blank");
                          });
                      }}
                      className="min-h-[36px] px-3 rounded-lg bg-emerald-600/15 hover:bg-emerald-600/25 border border-emerald-500/40 text-emerald-300 text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5"
                    >
                      <Landmark size={12} /> PDF Interno
                    </a>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowForm(false)}
        >
          <form
            data-testid={testIds.adminItemForm}
            onSubmit={submitItem}
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-lg bg-zinc-900 border border-zinc-800 rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-black uppercase text-white">
                {form.id ? "Modifica articolo" : "Nuovo articolo"}
              </h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Nome articolo
                </label>
                <input
                  data-testid={testIds.adminItemName}
                  required
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: e.target.value })
                  }
                  className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                    Categoria
                  </label>
                  <select
                    data-testid={testIds.adminItemCategory}
                    value={form.category}
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value })
                    }
                    className="w-full min-h-[44px] px-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                    Prezzo € / giorno
                  </label>
                  <input
                    data-testid={testIds.adminItemPrice}
                    required
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.daily_price}
                    onChange={(e) =>
                      setForm({ ...form, daily_price: e.target.value })
                    }
                    className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Quantità disponibile
                </label>
                <input
                  data-testid={testIds.adminItemQty}
                  required
                  type="number"
                  min={0}
                  value={form.quantity}
                  onChange={(e) =>
                    setForm({ ...form, quantity: e.target.value })
                  }
                  className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Descrizione
                </label>
                <textarea
                  data-testid={testIds.adminItemDesc}
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  className="w-full px-3.5 py-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm resize-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Foto articolo
                </label>
                <div className="flex items-center gap-3">
                  <div className="w-20 h-20 rounded-lg bg-zinc-950 border border-zinc-800 overflow-hidden flex items-center justify-center shrink-0">
                    {form.image_path ? (
                      <img
                        src={fileUrl(form.image_path)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Package size={22} className="text-zinc-700" />
                    )}
                  </div>
                  <label className="flex-1 min-h-[44px] flex items-center justify-center gap-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 font-semibold text-xs uppercase tracking-wider cursor-pointer transition">
                    {uploading ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Caricamento...
                      </>
                    ) : (
                      <>
                        <Upload size={14} />
                        {form.image_path ? "Cambia foto" : "Carica foto"}
                      </>
                    )}
                    <input
                      data-testid={testIds.adminItemImage}
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      onChange={uploadImage}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                  {form.image_path && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, image_path: null })}
                      className="w-9 h-9 rounded-lg bg-zinc-800 hover:bg-rose-950 text-zinc-400 hover:text-rose-400 flex items-center justify-center transition"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                data-testid={testIds.adminItemCancel}
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 min-h-[48px] rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-semibold text-xs uppercase tracking-wider transition"
              >
                Annulla
              </button>
              <button
                data-testid={testIds.adminItemSave}
                type="submit"
                className="flex-1 min-h-[48px] rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-heading font-black uppercase tracking-wider text-xs shadow-lg shadow-rose-900/40 transition"
              >
                {form.id ? "Salva" : "Aggiungi"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
